import { currentLoggedUserVar } from '@/shared/api/apollo/cache';
import { useCcmOnlyEnrollment } from '@/shared/lib/hooks/useCcmOnlyEnrollment';
import useShowMedicalServices from '@/shared/lib/hooks/useShowMedicalServices';
import useToken from '@/shared/lib/hooks/useToken';
import type { Role } from '@/shared/lib/types/admin';
import { MedicalServices } from '@/shared/lib/types/admin';
import { isProd } from '@/shared/lib/util/environment';
import { isModeProduction } from '@/shared/lib/util/modes';
import { useReactiveVar } from '@apollo/client';
import { useMemo } from 'react';
import type { MenuContext } from './menuCatalog';

/**
 * Everything the menu catalog needs to decide what this user sees.  Both the sidebar and the
 * store that feeds the ribbon build from this, so the two can't drift.
 */
const useMenuContext = (): MenuContext => {
  const { roleType } = useToken();
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const isMtmViewable = useShowMedicalServices(MedicalServices.MTM);
  const isRpmViewable = useShowMedicalServices(MedicalServices.RPM);
  const isTcmViewable = useShowMedicalServices(MedicalServices.TCM);
  const isCcmViewable = useShowMedicalServices(MedicalServices.CCM);
  const canEnrollCcmOnly = useCcmOnlyEnrollment() && isCcmViewable;

  return useMemo(
    () => ({
      role: (roleType as Role) ?? null,
      isMtmViewable,
      isRpmViewable,
      isTcmViewable,
      canEnrollCcmOnly,
      currentUser,
      isProdEnvironment: isProd(),
      isProductionMode: isModeProduction()
    }),
    [roleType, isMtmViewable, isRpmViewable, isTcmViewable, canEnrollCcmOnly, currentUser]
  );
};

export default useMenuContext;
